import { IMessagePart, ISourceReports, JsonBlock, ReportWhy, SearchResult, SearchResults } from 'app/concierge/types';
import { ReportWhyRequestData, ReportWhyResponse } from 'api/concierge';

/** The panel waits this long for the why texts; after it the icons go and the reports read as today. */
export const REPORT_WHY_TIMEOUT_MS = 20000;

const isSourceReports = (block: JsonBlock | undefined): block is ISourceReports =>
    block?.data_type === 'source_reports';
const isSearchResults = (block: JsonBlock | undefined): block is SearchResults => block?.data_type === 'search_results';
const isReportRow = (row: SearchResult) => row.type === 'dashboard_element';

/** The reports of a message that carry codes and no text yet: the ones the why request asks for. */
export function collectWhyReportIds(parts: IMessagePart[]): number[] {
    const ids: number[] = [];
    const add = (id: unknown, why?: ReportWhy) => {
        const n = Number(id);
        if (why && why.text === null && n > 0 && !ids.includes(n)) {
            ids.push(n);
        }
    };
    parts.forEach((part) => {
        (part.jsonBlocks ?? []).forEach((block) => {
            if (isSourceReports(block)) {
                (block.reports ?? []).forEach((report) => add(report.element_id, report.why));
            } else if (isSearchResults(block)) {
                (block.data ?? []).filter(isReportRow).forEach((row) => add(row.id, row.why));
            }
        });
    });

    return ids;
}

/** The names the panel already shows, for the summary's wording only. */
export function collectWhyReportNames(parts: IMessagePart[]): Record<number, string> {
    const names: Record<number, string> = {};
    parts.forEach((part) => {
        (part.jsonBlocks ?? []).forEach((block) => {
            if (isSourceReports(block)) {
                (block.reports ?? []).forEach((report) => {
                    if (report.why) names[Number(report.element_id)] = report.name;
                });
            } else if (isSearchResults(block)) {
                (block.data ?? []).filter(isReportRow).forEach((row) => {
                    if (row.why) names[Number(row.id)] = row.name;
                });
            }
        });
    });

    return names;
}

type WhyById = Record<number, ReportWhy | undefined>;

/** A new parts array where every report's why is replaced through `next` (undefined removes it). */
function mapWhy(
    parts: IMessagePart[],
    next: (id: number, why: ReportWhy | undefined) => ReportWhy | undefined,
): IMessagePart[] {
    const strip = <T extends { why?: ReportWhy }>(row: T, id: number): T => {
        const { why, ...rest } = row;
        const replaced = next(id, why);

        return (replaced ? { ...rest, why: replaced } : rest) as T;
    };

    return parts.map((part) => {
        if (!part.jsonBlocks?.length) {
            return part;
        }

        return {
            ...part,
            jsonBlocks: part.jsonBlocks.map((block) => {
                if (isSourceReports(block)) {
                    return {
                        ...block,
                        reports: (block.reports ?? []).map((report) => strip(report, Number(report.element_id))),
                    };
                }
                if (isSearchResults(block)) {
                    return { ...block, data: (block.data ?? []).map((row) => strip(row, Number(row.id))) };
                }

                return block;
            }),
        };
    });
}

/**
 * The why reply applied to the message: a report the reply worded gets its text; a report the
 * reply skipped, or worded with nothing, loses its why (no icon). Never mutates the input.
 */
export function applyReportWhyTexts(parts: IMessagePart[], reports: ReportWhyResponse['reports']): IMessagePart[] {
    const byId: WhyById = {};
    (reports ?? []).forEach((report) => {
        const id = Number(report.element_id);
        const text = report.why?.text;
        if (id > 0 && typeof text === 'string' && text.trim() !== '') {
            byId[id] = { reasons: report.why.reasons ?? [], text };
        }
    });

    return mapWhy(parts, (id, why) => (why ? byId[id] : undefined));
}

/** Timeout or error: every why goes, the reports read exactly as today. */
export function stripReportWhy(parts: IMessagePart[]): IMessagePart[] {
    return mapWhy(parts, () => undefined);
}

/**
 * The parallel section's rows arrive with their texts already composed on the server; a row whose
 * text never came loses its why so no icon stays in the preparing state.
 */
export function settleSearchResultsWhy(results: SearchResults): SearchResults {
    return {
        ...results,
        data: (results.data ?? []).map((row) => {
            if (!row.why || (typeof row.why.text === 'string' && row.why.text.trim() !== '')) {
                return row;
            }
            const { why, ...rest } = row;

            return rest as SearchResult;
        }),
    };
}

/**
 * One why request with the panel's timeout: the reply inside it, null after it (the request is
 * aborted) or on any error.
 */
export function requestReportWhy(
    fetcher: (params: ReportWhyRequestData, signal?: AbortSignal) => Promise<ReportWhyResponse>,
    params: ReportWhyRequestData,
    timeoutMs: number = REPORT_WHY_TIMEOUT_MS,
): Promise<ReportWhyResponse | null> {
    const controller = new AbortController();

    return new Promise<ReportWhyResponse | null>((resolve) => {
        const timer = setTimeout(() => {
            controller.abort();
            resolve(null);
        }, timeoutMs);
        fetcher(params, controller.signal)
            .then((response) => {
                clearTimeout(timer);
                resolve(response ?? null);
            })
            .catch(() => {
                clearTimeout(timer);
                resolve(null);
            });
    });
}
